/**
 * Path-based tab segments for detail surfaces.
 *
 * The detail pages used to carry their active tab in a `?tab=` query param.
 * They are path segments now - `/…/:id/:tab?` - matching Settings, Finance and
 * the Class-C detail pages. These helpers are the small, shared URL math so
 * every surface slugs, decodes and redirects the same way:
 *
 *   • A tab's canonical LABEL - what the switch runs on and the bar DISPLAYS -
 *     can contain a space ("Clause Rules") or mixed case ("Overview"). That is
 *     ugly in a URL, so the URL SEGMENT is a lowercase kebab-case SLUG of the
 *     label ("clause-rules", "overview"). `slugify` makes the slug; the label
 *     stays the component's, localized via `t()`.
 *   • A surface knows its own ordered tab vocabulary (the canonical labels).
 *     `resolveTabSlug` turns a URL slug back into the canonical label it came
 *     from (case-insensitively, and tolerating an old label spelled out in the
 *     URL), falling back to the first tab when the slug is missing or unknown -
 *     so a stale or renamed segment lands on the first tab, never on nothing.
 *   • A bookmark or deep link in the old `?tab=X` shape must still land on the
 *     right tab: `legacyTabRedirect` turns `/…/:id?tab=X` into `/…/:id/<slug>`
 *     (whatever case/spacing `X` had), preserving any OTHER query params.
 */

/** Decode a raw `:tab` route param (RR v5 does not decode params itself). */
export const decodeTab = (raw?: string): string | undefined =>
  raw ? decodeURIComponent(raw) : undefined;

/** A tab label's URL segment: lowercase, kebab-case, trimmed. */
export const slugify = (label: string): string =>
  label.trim().toLowerCase().replace(/\s+/g, '-');

/**
 * Resolve a URL slug back to the canonical tab label it was made from.
 *
 * `tabs` is the surface's ordered tab vocabulary (the canonical labels). The
 * match is by slug, so it is case-insensitive and also accepts an old label
 * spelled out in the URL ("Clause Rules" and "clause-rules" both resolve to
 * "Clause Rules"). A missing or unknown slug falls back to the FIRST tab, so a
 * stale or renamed segment never blanks the canvas - and there is no redirect
 * loop, because the fallback is a real tab.
 */
export const resolveTabSlug = (
  tabs: readonly string[],
  raw: string | null | undefined,
): string => {
  const fallback = tabs[0] ?? '';
  if (!raw) return fallback;
  const target = slugify(raw);
  return tabs.find((label) => slugify(label) === target) ?? fallback;
};

/** Build the path form of a tab URL: `<basePath>/<slug of the label>`. */
export const buildTabPath = (basePath: string, tab: string): string =>
  `${basePath}/${slugify(tab)}`;

/**
 * The redirect target for a legacy `?tab=` URL, or null when none applies.
 *
 * Returns null when a path tab is already present (so there is no redirect
 * loop) or when there is no `?tab=` to migrate. Any OTHER query params on the
 * URL are preserved on the path form.
 */
export const legacyTabRedirect = (
  basePath: string,
  tabParam: string | undefined,
  search: string,
): string | null => {
  if (tabParam) return null;
  const params = new URLSearchParams(search);
  const legacy = params.get('tab');
  if (!legacy) return null;
  params.delete('tab');
  const rest = params.toString();
  return `${buildTabPath(basePath, legacy)}${rest ? `?${rest}` : ''}`;
};

/**
 * The tab a surface should show for the current URL.
 *
 * Prefers the path segment; falls back to a legacy `?tab=` (so the brief flash
 * before `legacyTabRedirect` fires still shows the right tab); otherwise ''
 * which a surface's `resolveTab` turns into its first declared tab.
 */
export const tabFromUrl = (tabParam: string | undefined, search: string): string =>
  decodeTab(tabParam) ?? new URLSearchParams(search).get('tab') ?? '';
